import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { ArrowLeft, Check, Crown, Sparkles, AlertTriangle, Calendar, CreditCard, Mail, X, CheckCircle2, Info } from "lucide-react";
import { useAuth } from "../lib/auth-context";
import { api } from "../lib/api";
import { toast } from "sonner";

const STATUS_LABEL = {
  FREE: "Free",
  TRIAL: "7-day Pro Trial",
  PRO_ACTIVE: "Pro · Active",
  PRO_CANCELLED: "Pro · Cancelling at period end",
  PRO_EXPIRED: "Pro Expired",
  PAYMENT_FAILED: "Payment Failed · Grace Period",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso.slice(0, 10); }
};

const ConfirmModal = ({ open, title, body, bullets = [], confirmLabel, confirmTone = "primary", busy, onConfirm, onCancel }) => {
  if (!open) return null;
  const confirmClass = confirmTone === "danger"
    ? "bg-[#D6336C] hover:bg-[#b51e57] text-white"
    : "bg-[#234B38] hover:bg-[#1A382A] text-white";
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="confirm-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={!busy ? onCancel : undefined} />
      <div className="relative w-full max-w-sm rounded-3xl bg-white border border-[#E2EBE6] p-6 shadow-2xl">
        <button onClick={onCancel} disabled={busy} data-testid="confirm-close-btn"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#FDFBF7] hover:bg-[#E2EBE6] flex items-center justify-center">
          <X className="w-4 h-4 text-[#5C7568]" />
        </button>
        <h3 className="text-xl font-medium text-[#1A2E24]" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="confirm-title">
          {title}
        </h3>
        <p className="text-sm text-[#5C7568] mt-3 leading-relaxed" data-testid="confirm-body">{body}</p>
        {bullets.length > 0 && (
          <ul className="mt-3 space-y-1.5" data-testid="confirm-bullets">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-[#1A2E24]">
                <Check className="w-3.5 h-3.5 mt-0.5 text-[#234B38] shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2 mt-5">
          <button
            onClick={onCancel}
            disabled={busy}
            data-testid="confirm-cancel-btn"
            className="flex-1 rounded-full bg-white border border-[#E2EBE6] text-[#5C7568] py-3 text-sm font-medium hover:bg-[#FDFBF7] transition-colors"
          >
            Keep current
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-action-btn"
            className={`flex-1 rounded-full py-3 text-sm font-medium transition-colors disabled:opacity-60 ${confirmClass}`}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const SuccessModal = ({ open, title, body, bullets = [], onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="success-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-3xl bg-white border border-[#E2EBE6] p-6 shadow-2xl">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#234B38]/10 mx-auto mb-3">
          <CheckCircle2 className="w-7 h-7 text-[#234B38]" />
        </div>
        <h3 className="text-xl font-medium text-[#1A2E24] text-center" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="success-title">
          {title}
        </h3>
        <p className="text-sm text-[#5C7568] mt-2 leading-relaxed text-center" data-testid="success-body">{body}</p>
        {bullets.length > 0 && (
          <ul className="mt-4 space-y-2 rounded-2xl bg-[#FDFBF7] border border-[#E2EBE6] p-3">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-[#1A2E24]" data-testid={`success-bullet-${i}`}>
                <Check className="w-3.5 h-3.5 mt-0.5 text-[#234B38] shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
        <button
          onClick={onClose}
          data-testid="success-close-btn"
          className="mt-5 w-full rounded-full bg-[#234B38] hover:bg-[#1A382A] text-white py-3 text-sm font-medium transition-colors"
        >
          Got it
        </button>
      </div>
    </div>
  );
};

const Subscription = () => {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [keyId, setKeyId] = useState("");
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [history, setHistory] = useState([]);
  const [confirmState, setConfirmState] = useState({ open: false, kind: null, planType: null });
  const [successState, setSuccessState] = useState({ open: false, title: "", body: "", bullets: [] });

  const load = async () => {
    setLoading(true);
    try {
      const [p, s, h] = await Promise.all([api.listPlans(), api.subscriptionStatus(), api.subscriptionHistory()]);
      setPlans(p.data.plans || []);
      setKeyId(p.data.key_id);
      setSub(s.data);
      setHistory(h.data.items || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openRazorpay = (subscriptionId, planLabel) => {
    return new Promise((resolve) => {
      if (typeof window === "undefined" || !window.Razorpay) {
        toast.error("Payment checkout is loading. Please retry in a moment.");
        resolve(false); return;
      }
      const opts = {
        key: keyId,
        subscription_id: subscriptionId,
        name: "Daily Reset by Maayaveda",
        description: planLabel,
        prefill: {
          email: user?.email || "",
          contact: user?.phone || "",
          name: user?.name || "",
        },
        theme: { color: "#234B38" },
        handler: async () => {
          toast.success("Payment confirmed · activating Pro…");
          setTimeout(async () => {
            await refresh();
            await load();
            resolve(true);
          }, 1200);
        },
        modal: {
          ondismiss: () => {
            toast("Checkout closed — you can resume anytime from this page.");
            resolve(false);
          },
        },
      };
      const rz = new window.Razorpay(opts);
      rz.on("payment.failed", () => toast.error("Payment failed. Please try again or use a different method."));
      rz.open();
    });
  };

  const performSubscribe = async (planType) => {
    setWorking(true);
    try {
      const r = await api.createSubscription(planType);
      const label = planType === "monthly" ? "Daily Reset Pro Monthly · ₹149/mo" : "Daily Reset Pro Yearly · ₹999/yr";
      const ok = await openRazorpay(r.data.subscription_id, label);
      if (ok) {
        // Re-fetch fresh sub
        const s = await api.subscriptionStatus();
        const nb = s.data.next_billing_date;
        setSuccessState({
          open: true,
          title: "Welcome to Pro!",
          body: `Your ${planType === "monthly" ? "Monthly" : "Yearly"} subscription is active.`,
          bullets: [
            `Plan: ${planType === "monthly" ? "Daily Reset Pro Monthly · ₹149/mo" : "Daily Reset Pro Yearly · ₹999/yr"}`,
            nb ? `Next renewal: ${fmtDate(nb)}` : "Renews automatically each cycle",
            "All Pro features unlocked instantly",
            "Cancel anytime — keep access till the cycle ends",
          ],
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    } finally {
      setWorking(false);
    }
  };

  const performChangePlan = async (planType) => {
    setWorking(true);
    try {
      const r = await api.changePlan(planType);
      const label = planType === "monthly" ? "Daily Reset Pro Monthly · ₹149/mo" : "Daily Reset Pro Yearly · ₹999/yr";
      const scheduledStart = r.data.scheduled_start_date;
      toast("Authorize the new plan to confirm the switch", { description: "Your current plan stays active until its cycle ends." });
      const ok = await openRazorpay(r.data.subscription_id, label);
      await refresh();
      await load();
      if (ok) {
        setSuccessState({
          open: true,
          title: "Plan switch scheduled",
          body: `You're switching to ${planType === "monthly" ? "Monthly" : "Yearly"}.`,
          bullets: [
            "Your current plan stays active — no access lost",
            scheduledStart ? `New plan starts: ${fmtDate(scheduledStart)}` : "New plan starts after current cycle",
            `New plan: ${label}`,
            "First charge happens automatically on the start date",
            "View this anytime under \"Scheduled Plan\" below",
          ],
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Plan change failed");
    } finally {
      setWorking(false);
    }
  };

  const performCancel = async () => {
    setWorking(true);
    try {
      const r = await api.cancelSubscription();
      const endDate = r.data.next_billing_date;
      await refresh();
      await load();
      setSuccessState({
        open: true,
        title: "Subscription cancelled",
        body: "We're sorry to see you reduce. You won't be charged again.",
        bullets: [
          endDate ? `Pro access continues until: ${fmtDate(endDate)}` : "Pro stays active until current cycle ends",
          "No further charges will be made",
          "All your data (journal, sessions, streaks) is preserved",
          "Renew anytime from this page",
        ],
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally {
      setWorking(false);
    }
  };

  const askChangePlan = (planType) => setConfirmState({ open: true, kind: "change", planType });
  const askCancel = () => setConfirmState({ open: true, kind: "cancel", planType: null });
  const askRenew = (planType) => setConfirmState({ open: true, kind: "renew", planType });

  const onConfirm = async () => {
    const { kind, planType } = confirmState;
    setConfirmState({ ...confirmState, open: false });
    if (kind === "change") await performChangePlan(planType);
    else if (kind === "cancel") await performCancel();
    else if (kind === "renew") await performSubscribe(planType);
  };
  const onCancelModal = () => setConfirmState({ open: false, kind: null, planType: null });

  if (loading || !sub) {
    return (
      <div className="app-shell" data-testid="subscription-page">
        <Header />
        <p className="px-6 text-sm text-[#5C7568]">Loading…</p>
      </div>
    );
  }

  const status = sub.subscription_status;
  const currentPlanType = sub.plan_type;

  // Build confirm-modal content based on intent.
  let modalTitle = "", modalBody = "", modalLabel = "", modalTone = "primary", modalBullets = [];
  if (confirmState.kind === "change") {
    const newLabel = confirmState.planType === "monthly" ? "Monthly (₹149/mo)" : "Yearly (₹999/yr)";
    const oldLabel = currentPlanType === "monthly" ? "Monthly" : currentPlanType === "yearly" ? "Yearly" : "current";
    const cycleEnd = sub.next_billing_date ? fmtDate(sub.next_billing_date) : "your current cycle end";
    modalTitle = `Switch to ${newLabel}?`;
    modalBody = `Here's exactly what happens:`;
    modalBullets = [
      `Your current ${oldLabel} plan stays active until ${cycleEnd}`,
      `New ${newLabel} plan is scheduled to start the day after`,
      `You'll authorize the new plan now (no immediate charge)`,
      `First charge happens automatically on the start date`,
      `Recorded in Subscription History & Billing`,
    ];
    modalLabel = `Yes, schedule switch`;
  } else if (confirmState.kind === "cancel") {
    const cycleEnd = sub.next_billing_date ? fmtDate(sub.next_billing_date) : "your current cycle end";
    modalTitle = "Cancel subscription?";
    modalBody = "Here's exactly what happens:";
    modalBullets = [
      `Pro access continues until ${cycleEnd}`,
      "No further charges after cancellation",
      "All your data is preserved (journal, sessions, streaks)",
      "You can renew anytime from this page",
    ];
    modalLabel = "Yes, cancel";
    modalTone = "danger";
  } else if (confirmState.kind === "renew") {
    const newLabel = confirmState.planType === "monthly" ? "Monthly · ₹149/mo" : "Yearly · ₹999/yr";
    modalTitle = "Renew Pro?";
    modalBody = `Start a new ${newLabel} subscription.`;
    modalBullets = [
      "You'll be taken to Razorpay Checkout",
      "Pro access activates as soon as payment is confirmed",
      "Auto-renews each cycle — cancel anytime",
    ];
    modalLabel = "Continue to checkout";
  }

  // Persistent banner for scheduled plan
  const showScheduledBanner = !!sub.scheduled_plan_type;
  const showCancellingBanner = status === "PRO_CANCELLED" && !sub.scheduled_plan_type;

  return (
    <div className="app-shell" data-testid="subscription-page">
      <Header />
      <section className="px-6 pt-2 pb-6">
        <button onClick={() => navigate(-1)} className="text-xs text-[#5C7568] flex items-center gap-1 mb-3" data-testid="back-btn">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38]">Subscription</p>
        <h2 className="text-3xl font-medium text-[#1A2E24] mt-1 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          Manage your plan
        </h2>

        {/* Lifecycle banners */}
        {showScheduledBanner && (
          <div className="mt-4 rounded-2xl bg-[#F5D0D6]/40 border border-[#F5D0D6] px-4 py-3 flex items-start gap-2" data-testid="scheduled-banner">
            <Info className="w-4 h-4 text-[#D6336C] mt-0.5 shrink-0" />
            <div className="text-xs text-[#1A2E24] leading-relaxed">
              <span className="font-semibold">Plan switch scheduled.</span>{" "}
              Your current plan continues until <span className="font-semibold">{fmtDate(sub.next_billing_date)}</span>, then{" "}
              <span className="font-semibold">{sub.scheduled_plan_type === "yearly" ? "Yearly" : "Monthly"}</span> starts on{" "}
              <span className="font-semibold">{fmtDate(sub.scheduled_start_date)}</span>.
            </div>
          </div>
        )}
        {showCancellingBanner && sub.next_billing_date && (
          <div className="mt-4 rounded-2xl bg-[#FDFBF7] border border-[#E2EBE6] px-4 py-3 flex items-start gap-2" data-testid="cancelling-banner">
            <AlertTriangle className="w-4 h-4 text-[#D6336C] mt-0.5 shrink-0" />
            <div className="text-xs text-[#1A2E24] leading-relaxed">
              <span className="font-semibold">Cancellation in effect.</span>{" "}
              Pro access continues until <span className="font-semibold">{fmtDate(sub.next_billing_date)}</span>, no further charges.
            </div>
          </div>
        )}
        {status === "TRIAL" && (
          <div className="mt-4 rounded-2xl bg-[#234B38]/5 border border-[#234B38]/15 px-4 py-3 flex items-start gap-2" data-testid="trial-banner">
            <Sparkles className="w-4 h-4 text-[#D6336C] mt-0.5 shrink-0" />
            <div className="text-xs text-[#1A2E24] leading-relaxed">
              <span className="font-semibold">{sub.trial_days_remaining} day{sub.trial_days_remaining === 1 ? "" : "s"} left in your free trial.</span>{" "}
              Trial ends <span className="font-semibold">{fmtDate(sub.trial_end)}</span>. Pick a plan below to keep Pro.
            </div>
          </div>
        )}

        {/* Current status card */}
        <div className="mt-6 rounded-3xl bg-white border border-[#E2EBE6] p-5 shadow-[0_8px_32px_rgba(35,75,56,0.05)]" data-testid="current-status-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#5C7568]">Current Plan</p>
              <p className="text-lg font-medium text-[#1A2E24] mt-1 flex items-center gap-2" data-testid="current-plan-label" style={{ fontFamily: "Outfit, sans-serif" }}>
                {status === "TRIAL" && <Sparkles className="w-4 h-4 text-[#D6336C]" />}
                {(status === "PRO_ACTIVE" || status === "PRO_CANCELLED") && <Crown className="w-4 h-4 text-[#234B38]" />}
                {status === "PAYMENT_FAILED" && <AlertTriangle className="w-4 h-4 text-[#D6336C]" />}
                {STATUS_LABEL[status] || status}
              </p>
              {sub.amount_display && (
                <p className="text-xs text-[#5C7568] mt-1" data-testid="current-amount">{sub.amount_display}</p>
              )}
            </div>
            <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${
              status === "PRO_ACTIVE" ? "bg-[#234B38] text-white"
              : status === "TRIAL" ? "bg-[#F5D0D6] text-[#D6336C]"
              : status === "PRO_CANCELLED" ? "bg-[#F5D0D6]/60 text-[#D6336C]"
              : status === "PAYMENT_FAILED" ? "bg-[#D6336C] text-white"
              : "bg-[#E2EBE6] text-[#5C7568]"
            }`} data-testid="status-badge">
              {status}
            </span>
          </div>

          {/* Activation date for PRO_ACTIVE */}
          {status === "PRO_ACTIVE" && sub.activated_at && (
            <div className="flex items-center gap-2 mt-3 text-xs text-[#5C7568]" data-testid="activation-row">
              <Calendar className="w-3.5 h-3.5" />
              <span>Activated: <span className="font-semibold text-[#1A2E24]">{fmtDate(sub.activated_at)}</span></span>
            </div>
          )}

          {/* Renewal / access-end date */}
          {sub.next_billing_date && (
            <div className="flex items-center gap-2 mt-2 text-xs text-[#5C7568]" data-testid="renewal-row">
              <Calendar className="w-3.5 h-3.5" />
              <span>
                {status === "PRO_CANCELLED" ? "Access ends" : "Next renewal"}:{" "}
                <span className="font-semibold text-[#1A2E24]">{fmtDate(sub.next_billing_date)}</span>
              </span>
            </div>
          )}

          {sub.payment_method && (
            <div className="flex items-center gap-2 mt-2 text-xs text-[#5C7568]" data-testid="payment-method-row">
              <CreditCard className="w-3.5 h-3.5" />
              <span className="capitalize">Last paid via {sub.payment_method}</span>
            </div>
          )}

          {status === "PAYMENT_FAILED" && sub.grace_period_end && (
            <div className="mt-3 rounded-2xl bg-[#D6336C]/10 border border-[#D6336C]/30 p-3 text-xs text-[#1A2E24]" data-testid="grace-warning">
              <p className="font-semibold text-[#D6336C]">Payment failed.</p>
              <p>You have until <span className="font-semibold">{fmtDate(sub.grace_period_end)}</span> to update payment before Pro access ends. Razorpay will retry automatically.</p>
            </div>
          )}
        </div>

        {/* Scheduled (future-dated) plan card */}
        {sub.scheduled_plan_type && (
          <div className="mt-4 rounded-3xl bg-gradient-to-br from-[#F5D0D6]/30 to-white border border-[#F5D0D6] p-5" data-testid="scheduled-plan-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#D6336C]">Scheduled Plan</p>
                <p className="text-lg font-medium text-[#1A2E24] mt-1 flex items-center gap-2" data-testid="scheduled-plan-label" style={{ fontFamily: "Outfit, sans-serif" }}>
                  <Sparkles className="w-4 h-4 text-[#D6336C]" />
                  {sub.scheduled_plan_name || ("Daily Reset Pro " + (sub.scheduled_plan_type === "yearly" ? "Yearly" : "Monthly"))}
                </p>
                {sub.scheduled_amount_display && (
                  <p className="text-xs text-[#5C7568] mt-1">{sub.scheduled_amount_display}</p>
                )}
              </div>
              <span className="rounded-full bg-[#F5D0D6] text-[#D6336C] px-3 py-1 text-[10px] font-semibold uppercase tracking-wider">
                Pending
              </span>
            </div>
            {sub.scheduled_start_date && (
              <div className="flex items-center gap-2 mt-3 text-xs text-[#1A2E24]" data-testid="scheduled-start-row">
                <Calendar className="w-3.5 h-3.5 text-[#D6336C]" />
                <span>Starts: <span className="font-semibold">{fmtDate(sub.scheduled_start_date)}</span></span>
              </div>
            )}
            <p className="text-[11px] text-[#5C7568] mt-2 leading-relaxed">
              Your current plan stays active until its end date. The scheduled plan activates automatically the day after — no action needed.
            </p>
          </div>
        )}

        {/* Upgrade / change plan options */}
        {(status === "FREE" || status === "TRIAL" || status === "PRO_EXPIRED" || status === "PRO_ACTIVE" || status === "PRO_CANCELLED" || status === "PAYMENT_FAILED") && (
          <div className="mt-6 space-y-3" data-testid="plans-list">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38]">
              {status === "PRO_ACTIVE" || status === "PRO_CANCELLED" ? "Change plan" : "Upgrade to Pro"}
            </p>
            {plans.map((p) => {
              const isCurrent = currentPlanType === p.type && (status === "PRO_ACTIVE" || status === "PRO_CANCELLED");
              const isScheduled = sub.scheduled_plan_type === p.type;
              const isManaging = status === "PRO_ACTIVE" || status === "PRO_CANCELLED";
              return (
                <div
                  key={p.type}
                  data-testid={`plan-${p.type}`}
                  className={`rounded-3xl border p-5 ${
                    isCurrent ? "bg-[#234B38]/5 border-[#234B38]"
                    : isScheduled ? "bg-[#F5D0D6]/20 border-[#F5D0D6]"
                    : "bg-white border-[#E2EBE6]"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#234B38]">{p.type === "monthly" ? "Monthly" : "Yearly · Best value"}</p>
                      <h3 className="text-base font-medium text-[#1A2E24] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{p.name}</h3>
                      <p className="text-2xl font-medium text-[#234B38] mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>{p.amount_display}</p>
                      {p.type === "yearly" && (
                        <p className="text-[10px] text-[#D6336C] font-semibold uppercase tracking-wider mt-1">Save ₹789 vs monthly</p>
                      )}
                    </div>
                    {isCurrent && (
                      <span className="rounded-full bg-[#234B38] text-white text-[10px] font-semibold px-2.5 py-0.5 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Current
                      </span>
                    )}
                    {isScheduled && !isCurrent && (
                      <span className="rounded-full bg-[#F5D0D6] text-[#D6336C] text-[10px] font-semibold px-2.5 py-0.5 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Scheduled
                      </span>
                    )}
                  </div>
                  {!isCurrent && !isScheduled && (
                    <button
                      disabled={working}
                      onClick={() => {
                        if (isManaging) askChangePlan(p.type);
                        else if (status === "PRO_EXPIRED" || status === "PAYMENT_FAILED") askRenew(p.type);
                        else performSubscribe(p.type);
                      }}
                      data-testid={`subscribe-${p.type}-btn`}
                      className="mt-4 w-full rounded-full bg-[#234B38] hover:bg-[#1A382A] disabled:opacity-60 text-white py-3 font-medium transition-colors"
                    >
                      {isManaging
                        ? `Schedule switch to ${p.type === "monthly" ? "Monthly" : "Yearly"}`
                        : (status === "PRO_EXPIRED" || status === "PAYMENT_FAILED" ? `Renew · ${p.amount_display}` : `Subscribe · ${p.amount_display}`)}
                    </button>
                  )}
                  {isCurrent && (
                    <p className="text-[11px] text-[#5C7568] mt-3 leading-relaxed" data-testid={`current-${p.type}-note`}>
                      {status === "PRO_CANCELLED"
                        ? `Cancelling at end of cycle (${fmtDate(sub.next_billing_date)})`
                        : `Auto-renews on ${fmtDate(sub.next_billing_date)}`}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Cancel / Renew actions */}
        <div className="mt-6 space-y-3">
          {status === "PRO_ACTIVE" && (
            <button
              onClick={askCancel}
              disabled={working}
              data-testid="cancel-subscription-btn"
              className="w-full rounded-full bg-white border border-[#F5D0D6] text-[#D6336C] hover:bg-[#F5D0D6]/30 py-3.5 font-medium transition-colors"
            >
              Cancel subscription
            </button>
          )}
          {(status === "PRO_EXPIRED" || (status === "PRO_CANCELLED" && sub.next_billing_date && new Date(sub.next_billing_date) < new Date())) && (
            <button
              onClick={() => askRenew(currentPlanType || "monthly")}
              disabled={working}
              data-testid="renew-subscription-btn"
              className="w-full rounded-full bg-[#234B38] hover:bg-[#1A382A] text-white py-3.5 font-medium transition-colors"
            >
              Renew subscription
            </button>
          )}
        </div>

        {/* Subscription History */}
        {history.length > 0 && (
          <div className="mt-6" data-testid="subscription-history">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38] mb-3">Subscription History</p>
            <div className="rounded-3xl bg-white border border-[#E2EBE6] divide-y divide-[#E2EBE6]">
              {history.slice(0, 10).map((h, idx) => (
                <div key={idx} className="px-5 py-3" data-testid={`history-row-${idx}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-[#1A2E24]">{h.label}</p>
                    <span className="text-[10px] text-[#5C7568] uppercase tracking-wider shrink-0">
                      {h.timestamp ? h.timestamp.slice(0, 16).replace("T", " ") : ""}
                    </span>
                  </div>
                  {h.message && (
                    <p className="text-[11px] text-[#5C7568] mt-1 leading-relaxed">{h.message}</p>
                  )}
                  {h.amount_display && h.amount_display !== "—" && (
                    <p className="text-[11px] text-[#234B38] font-semibold mt-1">{h.amount_display}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment methods info */}
        <div className="mt-6 rounded-3xl bg-white border border-[#E2EBE6] p-5" data-testid="payment-methods-info">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#234B38]">Accepted Payment Methods</p>
          <p className="text-sm text-[#1A2E24] mt-2 leading-relaxed">
            UPI · Credit Cards · Debit Cards · Net Banking · Razorpay Wallets
          </p>
          <p className="text-[11px] text-[#5C7568] mt-2 leading-relaxed">
            Payments are processed securely by Razorpay. We never store your card number, CVV, or sensitive credentials.
          </p>
        </div>

        {/* Business + support */}
        <div className="mt-6 text-xs text-[#5C7568] leading-relaxed text-center" data-testid="business-info">
          <p>Billed by <span className="font-semibold text-[#1A2E24]">MRInfraTech Solutions (OPC) Pvt Ltd.</span></p>
          <p className="mt-1 flex items-center justify-center gap-1">
            <Mail className="w-3 h-3" />
            <a href="mailto:dailyreset@maayaveda.life" className="text-[#234B38] underline">dailyreset@maayaveda.life</a>
          </p>
        </div>
      </section>

      <ConfirmModal
        open={confirmState.open}
        title={modalTitle}
        body={modalBody}
        bullets={modalBullets}
        confirmLabel={modalLabel}
        confirmTone={modalTone}
        busy={working}
        onConfirm={onConfirm}
        onCancel={onCancelModal}
      />

      <SuccessModal
        open={successState.open}
        title={successState.title}
        body={successState.body}
        bullets={successState.bullets}
        onClose={() => setSuccessState({ open: false, title: "", body: "", bullets: [] })}
      />
    </div>
  );
};

export default Subscription;
